import { SocialPlatform } from '@mg/shared';

/**
 * The rules behind PlatformPreview, kept apart from the markup so each can be tested on its own:
 * which platforms get a preview, how much text each shows before cutting it, what the "see more"
 * control says, how hashtags are found, and which handle is worth printing.
 */

/** The platforms the composer can show as a feed post. */
export type PreviewPlatform =
  | SocialPlatform.LINKEDIN
  | SocialPlatform.FACEBOOK
  | SocialPlatform.INSTAGRAM
  | SocialPlatform.THREADS
  | SocialPlatform.X;

const PREVIEW_PLATFORMS: ReadonlySet<SocialPlatform> = new Set([
  SocialPlatform.LINKEDIN,
  SocialPlatform.FACEBOOK,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.THREADS,
  SocialPlatform.X,
]);

export function isPreviewPlatform(
  platform: SocialPlatform | null | undefined,
): platform is PreviewPlatform {
  return platform != null && PREVIEW_PLATFORMS.has(platform);
}

/** Who the post goes out as. */
export interface PreviewAccount {
  name: string;
  /** The account's handle as stored; only printed where it is a real @handle (see previewIdentity). */
  handle?: string | null;
}

export interface PreviewStyle {
  /**
   * How many characters of the post show before the platform cuts it, or null when the platform
   * shows the whole post. Roughly what each feed shows on a phone: LinkedIn about 210 characters,
   * Facebook about 477, Instagram 125 (the caption's first two lines).
   */
  collapseAt: number | null;
  /** The control that opens a cut post, worded as the platform words it. */
  seeMore: string | null;
  /** How the platform says the post went out a moment ago. */
  timeLabel: string;
}

export const PREVIEW_STYLE: Record<PreviewPlatform, PreviewStyle> = {
  [SocialPlatform.LINKEDIN]: { collapseAt: 210, seeMore: '…see more', timeLabel: 'Just now' },
  [SocialPlatform.FACEBOOK]: { collapseAt: 477, seeMore: '… See more', timeLabel: 'Just now' },
  [SocialPlatform.INSTAGRAM]: { collapseAt: 125, seeMore: 'more', timeLabel: 'Just now' },
  [SocialPlatform.THREADS]: { collapseAt: null, seeMore: null, timeLabel: 'now' },
  [SocialPlatform.X]: { collapseAt: null, seeMore: null, timeLabel: 'now' },
};

/** Closes an opened post again, on every platform that cuts. */
export const SHOW_LESS = 'show less';

// A hashtag the way the platforms find one: # then letters (with their accents and vowel signs),
// digits or underscores, at least one of them a letter - "#1" and "#2024" stay plain text, and so
// does the keycap emoji #️⃣ - and not glued to the end of a word or an address ("example.com/#top",
// "?ref=#spring" are links, not tags). An emoji's presentation selector before the # is no word, so
// "☀️#coffee" is a tag.
const TAG = '#(?![\\uFE0F\\u20E3])(?=[\\p{N}_]*[\\p{L}\\p{M}])[\\p{L}\\p{M}\\p{N}_]+';
const HASHTAG_SOURCE = `(?:(?<=[\\uFE0E\\uFE0F])|(?<![\\p{L}\\p{M}\\p{N}_/&#=-]))${TAG}`;
const WHOLE_HASHTAG = new RegExp(`^${TAG}$`, 'u');
// A web address: a # inside one is the page's anchor ("…/pricing?plan=#annual"), not a hashtag. The
// host starts with a letter or digit; the path may hold balanced (…) groups and ends at whitespace,
// an unbalanced ")" or CJK punctuation, so a tag right after an address still counts.
const URL_SOURCE =
  '(?:https?:\\/\\/|www\\.)[\\p{L}\\p{N}][\\p{L}\\p{N}.-]*(?::\\d+)?(?:[/?#](?:[^\\s()\\u3000-\\u303F\\uFF00-\\uFFEF]|\\([^\\s()]*\\))*)?';

/**
 * The run of hashtags that ends a post, split from the text before it, so the preview can set them
 * on a line of their own. Only that trailing run: a #tag inside a sentence is part of what the
 * sentence says and stays where it is (inline, in the link colour).
 */
export function splitTrailingHashtags(body: string): { text: string; hashtags: string[] } {
  const tokens = [...body.matchAll(/\S+/g)];
  let first = tokens.length;
  while (first > 0 && WHOLE_HASHTAG.test(tokens[first - 1][0])) first -= 1;
  if (first === tokens.length) return { text: body.trimEnd(), hashtags: [] };
  const cutAt = first === 0 ? 0 : (tokens[first].index ?? body.length);
  return {
    text: body.slice(0, cutAt).trimEnd(),
    hashtags: tokens.slice(first).map((t) => t[0]),
  };
}

/** Text split into plain runs and #hashtags, so a hashtag inside the text can take the link colour. */
export function hashtagRuns(text: string): Array<{ text: string; hashtag: boolean }> {
  const runs: Array<{ text: string; hashtag: boolean }> = [];
  const urls = [...text.matchAll(new RegExp(URL_SOURCE, 'giu'))].map((m) => {
    const from = m.index ?? 0;
    return { from, to: from + m[0].length };
  });
  let last = 0;
  for (const match of text.matchAll(new RegExp(HASHTAG_SOURCE, 'gu'))) {
    const at = match.index ?? 0;
    if (urls.some((u) => at >= u.from && at < u.to)) continue;
    if (at > last) runs.push({ text: text.slice(last, at), hashtag: false });
    runs.push({ text: match[0], hashtag: true });
    last = at + match[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last), hashtag: false });
  return runs;
}

/**
 * What a cut-down post shows: `text` trimmed to the platform's length at a word boundary, or the
 * whole text when only the hashtags push it over. `truncated` is whether anything is held back - the
 * hashtags are, whenever it is true.
 */
export function collapsedBody(
  text: string,
  hashtags: string[],
  limit: number | null,
): { shown: string; truncated: boolean } {
  const fullLength = text.length + (hashtags.length ? 1 + hashtags.join(' ').length : 0);
  if (limit == null || fullLength <= limit) return { shown: text, truncated: false };
  if (text.length <= limit) return { shown: text, truncated: true };
  const head = wholeCharactersWithin(text, limit);
  const lastBreak = head.search(/\s\S*$/);
  // Back up to the last word boundary, unless that would throw away most of the visible text.
  const cut = lastBreak > limit * 0.6 ? head.slice(0, lastBreak) : head;
  return { shown: cut.trimEnd(), truncated: true };
}

/**
 * The start of `text`, at most `limit` UTF-16 units long, without splitting a character: an emoji
 * cut in half draws as "\uFFFD", and a letter cut from its accent or vowel sign as something else.
 */
function wholeCharactersWithin(text: string, limit: number): string {
  if (typeof Intl.Segmenter !== 'function') {
    // Older browsers: at least never leave half of a surrogate pair.
    return text.slice(0, /[\uD800-\uDBFF]/.test(text[limit - 1] ?? '') ? limit - 1 : limit);
  }
  let end = 0;
  for (const { segment } of new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)) {
    if (end + segment.length > limit) break;
    end += segment.length;
  }
  return text.slice(0, end);
}

/**
 * The name the card leads with, and the @handle beside it, as each platform shows them. Only real
 * @handles are printed: on Facebook the stored "handle" is the Page name again, and on a LinkedIn
 * personal account it is the member's email address - neither appears in those feeds. Instagram
 * and Threads lead with the username itself; X shows the display name and then the @handle.
 */
export function previewIdentity(
  platform: PreviewPlatform,
  account: PreviewAccount,
): { name: string; handle: string | null } {
  const raw = account.handle?.trim() ?? '';
  const handle = /^@[^\s@]+$/.test(raw) ? raw : null;
  if (platform === SocialPlatform.INSTAGRAM || platform === SocialPlatform.THREADS) {
    return { name: handle ? handle.slice(1) : account.name, handle: null };
  }
  if (platform === SocialPlatform.X) return { name: account.name, handle };
  return { name: account.name, handle: null };
}
